import { LOW_SPOTS_RATIO } from '../constants'
import type {
  OrgRegistrationDivision,
  OrgRegistrationDivisionOption,
  OrgRegistrationKind,
} from '../types'

/**
 * How remaining spots read:
 * `available` — plenty left, `low` — at or under LOW_SPOTS_RATIO,
 * `full` — none left, `unlimited` — no cap.
 */
export type SpotsTone = 'available' | 'low' | 'full' | 'unlimited'

const isCapped = (
  capacity: number | null,
  spotsLeft: number | null
): capacity is number => capacity !== null && spotsLeft !== null && capacity > 0

export const getSpotsTone = (
  capacity: number | null,
  spotsLeft: number | null
): SpotsTone => {
  if (!isCapped(capacity, spotsLeft)) {
    return 'unlimited'
  }
  const left = Math.max(0, spotsLeft ?? 0)
  if (left === 0) {
    return 'full'
  }
  return left / capacity <= LOW_SPOTS_RATIO ? 'low' : 'available'
}

/** Share of a capped option already taken, 0–100; null when uncapped. */
export const getFilledPercent = (
  capacity: number | null,
  spotsLeft: number | null
): number | null => {
  if (!isCapped(capacity, spotsLeft)) {
    return null
  }
  const left = Math.min(capacity, Math.max(0, spotsLeft ?? 0))
  return Math.round(((capacity - left) / capacity) * 100)
}

/**
 * One option's spots, for a table cell whose column already names the kind:
 * "6 of 10 left", "Full", or "Unlimited".
 */
export const getSpotsLabel = (
  option: OrgRegistrationDivisionOption
): string => {
  const tone = getSpotsTone(option.capacity, option.spotsLeft)
  if (tone === 'unlimited') {
    return 'Unlimited'
  }
  if (tone === 'full') {
    return 'Full'
  }
  return `${option.spotsLeft} of ${option.capacity} left`
}

/** Spots left and capacity across one kind's capped options. */
export interface KindSpots {
  spotsLeft: number
  capacity: number
}

export interface SpotsSummary {
  /** Player spots across the capped player options; null when none is capped. */
  player: KindSpots | null
  /** Team spots across the capped team options; null when none is capped. */
  team: KindSpots | null
  /** Divisions whose every capped option is full. */
  fullCount: number
  tone: SpotsTone
}

const sumKind = (
  divisions: OrgRegistrationDivision[],
  kind: OrgRegistrationKind
): KindSpots | null => {
  const capped = divisions
    .map((division) => division[kind])
    .filter(
      (option): option is OrgRegistrationDivisionOption =>
        option !== null && isCapped(option.capacity, option.spotsLeft)
    )
  if (capped.length === 0) {
    return null
  }
  return {
    spotsLeft: capped.reduce(
      (sum, option) => sum + Math.max(0, option.spotsLeft ?? 0),
      0
    ),
    capacity: capped.reduce((sum, option) => sum + (option.capacity ?? 0), 0),
  }
}

/** A division is full when it has capped options and every one is full. */
const isDivisionFull = (division: OrgRegistrationDivision): boolean => {
  const capped = [division.player, division.team].filter(
    (option): option is OrgRegistrationDivisionOption =>
      option !== null && isCapped(option.capacity, option.spotsLeft)
  )
  return (
    capped.length > 0 &&
    capped.every(
      (option) => getSpotsTone(option.capacity, option.spotsLeft) === 'full'
    )
  )
}

/**
 * The whole registration's spots, player and team counted separately. Low when
 * either kind is at or under LOW_SPOTS_RATIO; full when both are.
 */
export const summarizeSpots = (
  divisions: OrgRegistrationDivision[]
): SpotsSummary => {
  const player = sumKind(divisions, 'player')
  const team = sumKind(divisions, 'team')
  const tones = [player, team]
    .filter((kind): kind is KindSpots => kind !== null)
    .map((kind) => getSpotsTone(kind.capacity, kind.spotsLeft))
  const tone: SpotsTone =
    tones.length === 0
      ? 'unlimited'
      : tones.every((value) => value === 'full')
      ? 'full'
      : tones.some((value) => value === 'low' || value === 'full')
      ? 'low'
      : 'available'
  return {
    player,
    team,
    fullCount: divisions.filter(isDivisionFull).length,
    tone,
  }
}

const spotsOf = (count: number, kind: OrgRegistrationKind): string =>
  `${count} ${kind} spot${count === 1 ? '' : 's'}`

/**
 * One line for the card: "4 team spots · 12 player spots left · 1 full",
 * "Full", or '' when nothing is capped.
 */
export const getSpotsSummaryLabel = (summary: SpotsSummary): string => {
  if (summary.tone === 'unlimited') {
    return ''
  }
  if (summary.tone === 'full') {
    return 'Full'
  }
  const parts = (['team', 'player'] as const)
    .filter((kind) => summary[kind] !== null)
    .map((kind) => spotsOf(summary[kind]?.spotsLeft ?? 0, kind))
  const left = `${parts.join(' · ')} left`
  return summary.fullCount > 0 ? `${left} · ${summary.fullCount} full` : left
}
